import Phaser from "phaser";
import { COLORS } from "../config";
import type { RegionDef } from "../types";

/**
 * Draws a recognizable, region-specific environment across the whole map so the
 * player feels they're *inside* that body part. Purely cosmetic, kept low-alpha
 * so enemy Strength numbers stay readable. Region-driven so Throat/Gut can each
 * get their own look later. All structures are static (drawn once at create).
 */
export function drawRegionBackground(scene: Phaser.Scene, region: RegionDef): void {
  const { width, height } = region.map;
  const key = `bg-${region.name.toLowerCase()}`;

  // If a region image was supplied (public/regions/<name>.jpg), use it: stretch
  // it over the whole map and lay a faint darkening tint so the white Strength
  // numbers stay readable.
  if (scene.textures.exists(key)) {
    const img = scene.add.image(width / 2, height / 2, key).setDepth(-20);
    // "Cover" fit: scale proportionally to fill the map (crop overflow) so the
    // image is never stretched/squished, whatever its source aspect ratio.
    const scale = Math.max(width / img.width, height / img.height);
    img.setScale(scale);
    scene.add
      .rectangle(width / 2, height / 2, width, height, 0x05080f, 0.18)
      .setDepth(-19);
    return;
  }

  // Fallback: procedural environment.
  const base = scene.add.graphics().setDepth(-20);
  base.fillGradientStyle(COLORS.bgTop, COLORS.bgTop, COLORS.bgBottom, COLORS.bgBottom, 1);
  base.fillRect(0, 0, width, height);

  const g = scene.add.graphics().setDepth(-15);
  switch (region.name) {
    case "Lungs":
      drawLungs(g, width, height);
      break;
    default:
      drawGenericTissue(g, width, height);
  }
}

// ── Lungs ──────────────────────────────────────────────────────────────────
// Two soft tissue lobes + a central airway branching into a bronchial tree,
// with grape-like alveoli clusters at the branch tips.

const TISSUE = 0xe89ab0; // warm rose tissue (low alpha over the cool gradient)
const AIRWAY = 0x9fe0ff; // pale bluish airway lining

function drawLungs(g: Phaser.GameObjects.Graphics, w: number, h: number): void {
  // Lobe silhouettes — overlapping ellipses give an organic, breathing shape.
  const lobeY = h * 0.46;
  const lobeW = w * 0.46;
  const lobeH = h * 0.74;
  for (const cx of [w * 0.29, w * 0.71]) {
    g.fillStyle(TISSUE, 0.06);
    g.fillEllipse(cx, lobeY, lobeW, lobeH);
    g.fillEllipse(cx, lobeY - lobeH * 0.18, lobeW * 0.82, lobeH * 0.5);
    g.lineStyle(3, TISSUE, 0.1);
    g.strokeEllipse(cx, lobeY, lobeW, lobeH);
  }

  // Trachea down the centre, splitting at the carina into a main bronchus per lobe.
  const topY = h * 0.05;
  const carinaY = h * 0.26;
  g.lineStyle(22, AIRWAY, 0.14);
  g.lineBetween(w / 2, topY, w / 2, carinaY);
  g.fillStyle(AIRWAY, 0.14);
  g.fillCircle(w / 2, carinaY, 12);

  branch(g, w / 2, carinaY, Phaser.Math.DegToRad(150), h * 0.16, 16, 5); // left lung
  branch(g, w / 2, carinaY, Phaser.Math.DegToRad(30), h * 0.16, 16, 5); // right lung
}

/** Recursive bronchial branch; sprouts an alveoli cluster as it thins out. */
function branch(
  g: Phaser.GameObjects.Graphics,
  x: number,
  y: number,
  angle: number,
  length: number,
  width: number,
  depth: number,
): void {
  if (depth <= 0 || width < 2) return;
  const x2 = x + Math.cos(angle) * length;
  const y2 = y + Math.sin(angle) * length;

  g.lineStyle(width, AIRWAY, 0.13);
  g.lineBetween(x, y, x2, y2);
  g.fillStyle(AIRWAY, 0.13);
  g.fillCircle(x2, y2, width * 0.5); // rounded joint

  if (depth <= 2) alveoliCluster(g, x2, y2, width * 1.6);

  const spread = Phaser.Math.DegToRad(26);
  const drift = Phaser.Math.FloatBetween(-0.12, 0.12);
  branch(g, x2, y2, angle - spread + drift, length * 0.74, width * 0.7, depth - 1);
  branch(g, x2, y2, angle + spread + drift, length * 0.74, width * 0.7, depth - 1);
}

/** A grape-like bundle of alveoli sacs. */
function alveoliCluster(g: Phaser.GameObjects.Graphics, x: number, y: number, r: number): void {
  g.fillStyle(TISSUE, 0.1);
  const sacs = 6;
  for (let i = 0; i < sacs; i++) {
    const a = (i / sacs) * Math.PI * 2;
    g.fillCircle(x + Math.cos(a) * r, y + Math.sin(a) * r, r * 0.7);
  }
  g.fillCircle(x, y, r * 0.8);
}

// ── Fallback for regions not yet styled ──────────────────────────────────────
function drawGenericTissue(g: Phaser.GameObjects.Graphics, w: number, h: number): void {
  g.fillStyle(0xffffff, 0.04);
  const count = Math.round((w * h) / 90000);
  for (let i = 0; i < count; i++) {
    g.fillCircle(Phaser.Math.Between(0, w), Phaser.Math.Between(0, h), Phaser.Math.Between(40, 120));
  }
}
